// ai-sidebar/components/launcher.tsx
// States 1 (launchpad, has history) and 6 (first open, no history).
// Greeting copy + styling track the design (Direction D, listeningBody);
// the suggestion list stays page-contextual (spec section 9).
import { MessageSquare, Wand2, Clock, ArrowUpRight } from "lucide-react";
import { useAssistant } from "../use-assistant";
import { pageContent } from "../suggestions";
import type { Suggestion } from "../types";

function relativeTime(iso?: string): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 172800) return "Yesterday";
  return `${Math.floor(s / 86400)}d`;
}

function SuggestionRow({ s, onPick }: { s: Suggestion; onPick: (text: string) => void }) {
  const isDo = s.kind === "do";
  const Icon = isDo ? Wand2 : MessageSquare;
  return (
    <button
      type="button"
      onClick={() => onPick(s.prompt ?? s.label)}
      className="ai-lift w-full flex items-center gap-3 border border-border bg-card text-left"
      style={{ padding: "11px 12px", borderRadius: 11, boxShadow: "var(--elevation-sm)" }}
    >
      <span
        className="flex items-center justify-center shrink-0"
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: isDo ? "var(--spectral-violet-10)" : "var(--muted)",
        }}
      >
        <Icon
          style={{ width: 15, height: 15, strokeWidth: 1.85, color: isDo ? "var(--spectral-violet)" : "var(--muted-foreground)" }}
        />
      </span>
      <span className="flex-1 min-w-0 text-foreground" style={{ fontSize: 13, fontWeight: 500, lineHeight: 1.4 }}>
        {s.label}
      </span>
      <ArrowUpRight style={{ width: 14, height: 14, strokeWidth: 1.85, color: "var(--silver-mist)" }} />
    </button>
  );
}

export function Launcher() {
  const { recent, send, resumeConversation, currentPage } = useAssistant();
  const content = pageContent(currentPage);
  const hasRecent = recent.length > 0;

  return (
    <div className="space-y-0" style={{ padding: 18 }}>
      <div
        className="text-foreground"
        style={{ fontSize: 15, fontWeight: 600, letterSpacing: "-0.01em" }}
      >
        {hasRecent ? "What can I help you figure out?" : "Welcome, I'm your Upmode Assistant."}
      </div>
      <div className="text-muted-foreground" style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 3 }}>
        {hasRecent
          ? "Ask about your data, or have me take an action."
          : "Ask about how AI sees your brand, or have me take an action. Here are good places to start."}
      </div>

      <div
        className="text-muted-foreground uppercase"
        style={{ margin: "18px 0 10px", fontSize: 11, fontWeight: 700, letterSpacing: "0.08em" }}
      >
        Suggested
      </div>
      <div className="flex flex-col" style={{ gap: 8 }}>
        {content.suggestions.map((s, i) => (
          <SuggestionRow key={i} s={s} onPick={send} />
        ))}
      </div>

      {hasRecent ? (
        <>
          <div className="flex items-center justify-between" style={{ margin: "20px 0 9px" }}>
            <div
              className="text-muted-foreground uppercase"
              style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em" }}
            >
              Recent
            </div>
            <button
              type="button"
              style={{ fontSize: 11, fontWeight: 600, color: "var(--spectral-violet)" }}
              onClick={() => {/* View-all surface is a later state; no-op for now */}}
            >
              View all
            </button>
          </div>
          <div className="space-y-1">
            {recent.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => resumeConversation(c.id)}
                className="ai-lift w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-muted"
              >
                <Clock className="w-3.5 h-3.5 shrink-0 text-muted-foreground" style={{ strokeWidth: 1.85 }} />
                <span className="text-sm text-foreground truncate flex-1">{c.title || "Conversation"}</span>
                {relativeTime(c.updated_at) && (
                  <span className="text-xs text-muted-foreground shrink-0">{relativeTime(c.updated_at)}</span>
                )}
              </button>
            ))}
          </div>
        </>
      ) : (
        <div
          className="rounded-xl border border-dashed border-border px-3 py-4 text-center"
          style={{ marginTop: 18 }}
        >
          <p className="text-xs text-muted-foreground">
            Your conversations will show up here once you start asking.
          </p>
        </div>
      )}
    </div>
  );
}
